import {ActionList, Heading, Tooltip} from '@primer/react'
import styles from './GroupsList.module.css'
import {clsx} from 'clsx'
import {useRef} from 'react'
import {useFileTreeTooltip} from '@github-ui/use-file-tree-tooltip'
import {usePullRequestAnalytics} from '../../hooks/use-pull-request-analytics'
import type {Group} from '../../types/group-types'
import {GroupsFeedback} from '../groups-feedback/GroupsFeedback'

/**
 * Props for the GroupsList component.
 */
interface GroupsListProps {
  /** Array of groups to display in the list */
  groups: Group[]
  /** ID of the currently active/selected group, undefined if "All files" is selected */
  activeGroupId?: number
  /** Callback function called when a group is selected */
  onSelectGroup: (groupId: number) => void
  /** Callback function called when "All files" is selected to clear the active group */
  onClearActiveGroup: () => void
}

/**
 * A list component that displays filterable groups for the file tree.
 * Renders an "All files" option followed by groups sorted by priority.
 * If no groups are provided, the component returns null.
 *
 * @returns The rendered groups list or null if no groups are provided
 */
export function GroupsList({groups, activeGroupId, onSelectGroup, onClearActiveGroup}: GroupsListProps) {
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()
  if (groups.length === 0) return null

  const handleClearActiveGroup = () => {
    sendPullRequestAnalyticsEvent('groups_list.clear_group', 'GROUPS_LIST_ITEM')
    onClearActiveGroup()
  }

  return (
    <div className={styles.GroupsListContainer}>
      <Heading as="h2" className="h6 fgColor-muted d-flex flex-items-center flex-justify-between gap-2">
        Groups by Copilot
        <GroupsFeedback />
      </Heading>
      <ActionList>
        <ActionList.Item active={activeGroupId === undefined} key="All files" onSelect={handleClearActiveGroup}>
          All files
        </ActionList.Item>
        {groups
          .sort((a, b) => a.priority - b.priority)
          .map(group => (
            <GroupItem key={group.id} group={group} activeGroupId={activeGroupId} onSelectGroup={onSelectGroup} />
          ))}
      </ActionList>
    </div>
  )
}

/**
 * Individual group item component that renders a single group in the list.
 * Includes tooltip functionality when the item is focused or hovered.
 */
function GroupItem({
  group,
  activeGroupId,
  onSelectGroup,
}: {
  group: Group
  activeGroupId?: number
  onSelectGroup: (groupId: number) => void
}) {
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()
  const itemRef = useRef<HTMLLIElement>(null)
  const showTooltip = useFileTreeTooltip({focusRowRef: itemRef, mouseRowRef: itemRef})
  const handleSelect = () => {
    sendPullRequestAnalyticsEvent('groups_list.select_group', 'GROUPS_LIST_ITEM')
    onSelectGroup(group.id)
  }
  const listItem = (
    <ActionList.Item active={activeGroupId === group.id} key={group.name} onSelect={handleSelect} ref={itemRef}>
      <div className={clsx(styles.GroupItem, 'PRIVATE_TreeView-item-content-text')}>{group.name}</div>
    </ActionList.Item>
  )

  if (showTooltip) {
    return (
      <Tooltip text={group.name} direction="ne">
        {listItem}
      </Tooltip>
    )
  }

  return listItem
}

try{ GroupsList.displayName ||= 'GroupsList' } catch {}
try{ GroupItem.displayName ||= 'GroupItem' } catch {}